import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TimesheetReminderSettings } from '@/components/TimesheetReminderSettings';
import type { 
  TimesheetDeadlineSettings as DeadlineSettingsType,
  OrganisationTimesheetSettings
} from '@/types/timesheetSettings';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import { toast } from 'sonner';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';

interface NotificationsPageProps {
  onNavigate?: (path: string) => void;
}

export const NotificationsPage: React.FC<NotificationsPageProps> = ({ onNavigate }) => {
  const { mode, isDemo, isLive } = useScreenDataMode('settings-notifications');
  
  const [deadlineSettings, setDeadlineSettings] = useState<DeadlineSettingsType | null>(null);
  const [fullSettings, setFullSettings] = useState<OrganisationTimesheetSettings | null>(null);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleReminderChange = useCallback((settings: DeadlineSettingsType, isValid: boolean) => {
    setDeadlineSettings(settings);
    setHasUnsavedChanges(true);
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isDemo) {
        // Demo mode - load from localStorage
        const stored = localStorage.getItem('demo-timesheet-settings');
        if (stored) {
          const settings = JSON.parse(stored) as OrganisationTimesheetSettings;
          setFullSettings(settings);
          setDeadlineSettings(settings.deadline);
        } else {
          // Use defaults
          const defaults = DEFAULT_TIMESHEET_SETTINGS;
          setFullSettings(defaults);
          setDeadlineSettings(defaults.deadline);
        }
      } else if (isLive) {
        // Live mode - fetch from API
        const settings = await settingsRepository.getOrganisationTimesheetSettings(mode);
        setFullSettings(settings);
        setRowVersion(settings.rowVersion);
        setDeadlineSettings(settings.deadline);
      }
    } catch (err) {
      console.error('Failed to load notification settings:', err);
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to load notification settings';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isDemo, isLive]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Browser warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSaveSettings = async () => {
    if (!deadlineSettings || !fullSettings) return;

    setIsSaving(true);
    try {
      const updatedDeadline = {
        ...fullSettings.deadline,
        sendReminders: deadlineSettings.sendReminders,
        reminderDaysBefore: deadlineSettings.reminderDaysBefore,
      };

      if (isDemo) {
        // Demo mode - save to localStorage
        const updatedSettings: OrganisationTimesheetSettings = {
          ...fullSettings,
          deadline: updatedDeadline,
        };
        localStorage.setItem('demo-timesheet-settings', JSON.stringify(updatedSettings));
        setFullSettings(updatedSettings);
        toast.success('Notification settings saved successfully');
      } else if (isLive) {
        // Live mode - save to API
        const result = await settingsRepository.updateDeadlineSettings(
          updatedDeadline,
          fullSettings,
          rowVersion,
          mode
        );
        setFullSettings(result);
        setRowVersion(result.rowVersion);
        toast.success('Notification settings saved successfully');
      }

      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save notification settings:', err);
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to save notification settings';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
      <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title="Notification Settings"
          subtitle="Configure how and when users are notified about timesheet-related events"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Bell />}
        />

        {/* Unsaved Changes Alert */}
        {hasUnsavedChanges && (
          <Alert variant="warning">
            <AlertDescription>
              You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.
            </AlertDescription>
          </Alert>
        )}

        {/* Reminder Settings */}
        {deadlineSettings && (
          <TimesheetReminderSettings
            settings={deadlineSettings}
            onChange={handleReminderChange}
          />
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            onClick={handleSaveSettings}
            disabled={!hasUnsavedChanges || isSaving}
            size="lg"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </div>
  );
};
